import { useEffect, useMemo, useState } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  Check,
  X,
  Loader2,
  Tags,
  Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useEventCategories,
  useCreateEventCategory,
  useUpdateEventCategory,
  useDeleteEventCategory,
  useUpdateVendorEvent,
} from '@/lib/api/queries'
import type { EventCategory, Vendor, VendorEvent } from '@/lib/api/types'

interface EventCategoriesPanelProps {
  eventId: number
  locations: VendorEvent[]
  canEdit: boolean
}

// Location name badges shown in the list row before overflowing to "+N more"
const BADGE_LIMIT = 4

const locationLabel = (loc: VendorEvent) => loc.booth || loc.name || `Location ${loc.id}`
const vendorLabel = (loc: VendorEvent) =>
  typeof loc.vendor === 'object' ? (loc.vendor as Vendor)?.name ?? null : null
const categoriesOf = (loc: VendorEvent) => loc.categories ?? []

// Manage the event's vendor categories. The list is a preview; creating,
// renaming, assigning locations and deleting all happen in the modal.
export function EventCategoriesPanel({ eventId, locations, canEdit }: EventCategoriesPanelProps) {
  const { data: categoriesData, isLoading } = useEventCategories(eventId)
  const categories = categoriesData?.results ?? []

  const updateCategory = useUpdateEventCategory()
  const deleteCategory = useDeleteEventCategory()

  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; category: EventCategory } | null>(null)
  const [isReordering, setIsReordering] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const locationsIn = (categoryId: number) =>
    locations.filter((l) => categoriesOf(l).includes(categoryId))

  // Move a category one step and persist a clean 0..n-1 order for every row
  // whose position changed (defaults are all 0, so swapping two order values
  // alone would often be a no-op).
  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= categories.length) return
    const reordered = [...categories]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(target, 0, moved)

    setIsReordering(true)
    setError(null)
    try {
      for (let i = 0; i < reordered.length; i++) {
        if (reordered[i].order !== i) {
          await updateCategory.mutateAsync({ eventId, categoryId: reordered[i].id, data: { order: i } })
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder categories')
    } finally {
      setIsReordering(false)
    }
  }

  const busy = isReordering || updateCategory.isPending || deleteCategory.isPending

  if (isLoading) {
    return (
      <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg p-6">
        <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading categories...
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden shadow-sm">
        <div className="bg-white dark:bg-[#131313] px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex flex-wrap items-start justify-between gap-3">
          <p className="text-sm text-neutral-600 dark:text-neutral-400 max-w-2xl">
            Group locations into categories. Open a category to rename it or change which locations
            it holds; a Home Layout "Vendors List" tile can then filter to one category.
          </p>
          {canEdit && (
            <Button onClick={() => setModal({ mode: 'create' })}>
              <Plus className="w-4 h-4" />
              New Category
            </Button>
          )}
        </div>

        {error && (
          <div className="px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {categories.length === 0 ? (
          <div className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400">
            <Tags className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
            <p className="text-sm">No vendor categories yet.</p>
            {canEdit && <p className="text-sm mt-1">Create one to start grouping locations.</p>}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-950">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Locations
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
              {categories.map((cat, index) => {
                const members = locationsIn(cat.id)
                const shown = members.slice(0, BADGE_LIMIT)
                const overflow = members.length - shown.length
                return (
                  <tr key={cat.id}>
                    <td className="px-6 py-3">
                      <button
                        onClick={() => setModal({ mode: 'edit', category: cat })}
                        className="text-sm font-medium text-neutral-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-500"
                      >
                        {cat.name}
                      </button>
                    </td>
                    <td className="px-6 py-3">
                      {members.length === 0 ? (
                        <span className="text-sm text-neutral-400 dark:text-neutral-500">
                          No locations
                        </span>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {shown.map((loc) => (
                            <span
                              key={loc.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
                            >
                              {locationLabel(loc)}
                            </span>
                          ))}
                          {overflow > 0 && (
                            <button
                              onClick={() => setModal({ mode: 'edit', category: cat })}
                              className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-500"
                            >
                              +{overflow} more
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleMove(index, -1)}
                              disabled={index === 0 || busy}
                              className="p-1.5 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded disabled:opacity-30"
                              title="Move up"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleMove(index, 1)}
                              disabled={index === categories.length - 1 || busy}
                              className="p-1.5 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded disabled:opacity-30"
                              title="Move down"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setModal({ mode: 'edit', category: cat })}
                          className="p-1.5 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded"
                          title={canEdit ? 'Edit category' : 'View category'}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <CategoryModal
          eventId={eventId}
          locations={locations}
          canEdit={canEdit}
          category={modal.mode === 'edit' ? modal.category : null}
          onClose={() => setModal(null)}
        />
      )}
    </>
  )
}

interface CategoryModalProps {
  eventId: number
  locations: VendorEvent[]
  canEdit: boolean
  category: EventCategory | null
  onClose: () => void
}

// Create or edit one category: name, which locations belong to it, and delete.
// Changes are staged locally and applied on save, so Cancel really cancels.
function CategoryModal({ eventId, locations, canEdit, category, onClose }: CategoryModalProps) {
  const createCategory = useCreateEventCategory()
  const updateCategory = useUpdateEventCategory()
  const deleteCategory = useDeleteEventCategory()
  const updateVendorEvent = useUpdateVendorEvent()

  const originalIds = useMemo(
    () =>
      category ? locations.filter((l) => categoriesOf(l).includes(category.id)).map((l) => l.id) : [],
    [category, locations],
  )

  const [name, setName] = useState(category?.name ?? '')
  const [selectedIds, setSelectedIds] = useState<number[]>(originalIds)
  const [locationSearch, setLocationSearch] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose()
    }
    document.addEventListener('keydown', handleEscape)
    document.body.classList.add('overflow-hidden')
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [onClose, isSaving])

  // Members first, then the rest alphabetically — the category's contents read
  // as a preview at the top of the picker.
  const pickerLocations = useMemo(() => {
    const term = locationSearch.trim().toLowerCase()
    return [...locations]
      .filter((l) => {
        if (!term) return true
        return (
          locationLabel(l).toLowerCase().includes(term) ||
          (vendorLabel(l) ?? '').toLowerCase().includes(term)
        )
      })
      .sort((a, b) => {
        const aIn = originalIds.includes(a.id)
        const bIn = originalIds.includes(b.id)
        if (aIn !== bIn) return aIn ? -1 : 1
        return locationLabel(a).localeCompare(locationLabel(b))
      })
  }, [locations, originalIds, locationSearch])

  const toggleLocation = (locId: number) => {
    setSelectedIds((prev) =>
      prev.includes(locId) ? prev.filter((id) => id !== locId) : [...prev, locId],
    )
  }

  // Apply a category id change to one location by rewriting its full
  // categories list — the backend M2M replaces the set on PATCH.
  const applyMembership = async (loc: VendorEvent, categoryId: number, shouldBelong: boolean) => {
    const current = categoriesOf(loc)
    const next = shouldBelong
      ? [...current, categoryId]
      : current.filter((id) => id !== categoryId)
    await updateVendorEvent.mutateAsync({ vendorEventId: loc.id, data: { categories: next }, eventId })
  }

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Category name is required')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      let categoryId = category?.id
      if (category) {
        if (trimmed !== category.name) {
          await updateCategory.mutateAsync({ eventId, categoryId: category.id, data: { name: trimmed } })
        }
      } else {
        const created = await createCategory.mutateAsync({ eventId, name: trimmed })
        categoryId = created.id
      }

      if (categoryId != null) {
        const added = locations.filter((l) => selectedIds.includes(l.id) && !originalIds.includes(l.id))
        const removed = locations.filter((l) => !selectedIds.includes(l.id) && originalIds.includes(l.id))
        for (const loc of added) await applyMembership(loc, categoryId, true)
        for (const loc of removed) await applyMembership(loc, categoryId, false)
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!category) return
    const count = originalIds.length
    const usage = count > 0 ? ` It is assigned to ${count} location${count === 1 ? '' : 's'}.` : ''
    if (
      !confirm(
        `Delete "${category.name}"?${usage} Any Home Layout tile filtering on it will fall back to showing all vendors.`,
      )
    )
      return

    setIsSaving(true)
    setError(null)
    try {
      await deleteCategory.mutateAsync({ eventId, categoryId: category.id })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category')
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={isSaving ? undefined : onClose} />
      <div
        className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {category ? category.name : 'New Category'}
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              {canEdit
                ? 'Name the category and choose which locations belong to it'
                : 'Locations in this category'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          <div>
            <label
              htmlFor="category-name"
              className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
            >
              Name {canEdit && <span className="text-red-500">*</span>}
            </label>
            <Input
              id="category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Bouffe / Food"
              disabled={!canEdit || isSaving}
              autoFocus={!category}
            />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Attendees see this name, so write it the way it should appear (both languages if the
              event is bilingual).
            </p>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Locations
                <span className="ml-2 font-normal text-neutral-500 dark:text-neutral-400">
                  {selectedIds.length} of {locations.length} selected
                </span>
              </label>
              {locations.length > 6 && (
                <div className="relative w-full sm:w-56">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    type="search"
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    placeholder="Filter locations"
                    className="pl-9"
                  />
                </div>
              )}
            </div>

            {locations.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                This event has no locations yet.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(canEdit ? pickerLocations : pickerLocations.filter((l) => selectedIds.includes(l.id))).map(
                  (loc) => {
                    const selected = selectedIds.includes(loc.id)
                    const vendor = vendorLabel(loc)
                    const label = locationLabel(loc)
                    return (
                      <button
                        key={loc.id}
                        type="button"
                        onClick={() => canEdit && toggleLocation(loc.id)}
                        disabled={!canEdit || isSaving}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors disabled:opacity-60 ${
                          selected
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-500'
                        } ${canEdit ? '' : 'cursor-default'}`}
                        title={vendor && vendor !== label ? `${vendor} — ${label}` : label}
                      >
                        {selected ? (
                          <Check className="w-3.5 h-3.5" />
                        ) : (
                          canEdit && <Plus className="w-3.5 h-3.5 text-neutral-400" />
                        )}
                        <span>{label}</span>
                        {vendor && vendor !== label && (
                          <span
                            className={
                              selected ? 'text-blue-100 text-xs' : 'text-neutral-400 dark:text-neutral-500 text-xs'
                            }
                          >
                            {vendor}
                          </span>
                        )}
                      </button>
                    )
                  },
                )}
                {canEdit && pickerLocations.length === 0 && (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    No locations match "{locationSearch.trim()}".
                  </p>
                )}
                {!canEdit && selectedIds.length === 0 && (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    No locations in this category yet.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer — mirrors the location modal's action row */}
        <div className="p-6 border-t border-neutral-200 dark:border-neutral-800 flex flex-col-reverse sm:flex-row sm:items-center gap-2 sm:gap-3">
          {canEdit && category && (
            <Button
              variant="outline"
              type="button"
              onClick={handleDelete}
              disabled={isSaving}
              className="w-full sm:w-auto text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 sm:mr-auto"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto sm:ml-auto">
            <Button
              variant="outline"
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {canEdit ? 'Cancel' : 'Close'}
            </Button>
            {canEdit && (
              <Button type="button" onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto">
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : category ? (
                  'Save Changes'
                ) : (
                  'Create Category'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
